import { useCallback, useEffect, useState } from "react";
import { Button } from "../../common/ui/widgets";
import { Popup } from "../../popup";
import { FeatureToggleWrapper } from "./styles";
import { homeServices } from "../services";
import { useDispatch, useSelector } from "react-redux";
import { userConstants } from "../../user/Constants";
import { decryptData, encryptData } from "../../common/utils/jsUtils";
import appService from "../../app/services";
import { useTranslation } from "react-i18next";

function FeatureToggle(props) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [featuresToggles, setFeaturesToggles] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const checkAndFetchFeatures = useCallback(async () => {
    try {
      const stored = decryptData(localStorage.getItem("featuresToggles"));
      const toggles = stored ? JSON.parse(stored) : null;

      const eulaFeature = toggles?.find(toggle =>
        toggle.feature === "EULA" 
      );

      if (!eulaFeature) {
        setIsLoading(true);
        const response = await appService.getFeatures();
        if (response?.features) {
          localStorage.setItem("featuresToggles", encryptData(JSON.stringify(response.features)));
          setFeaturesToggles(response.features);
          setData(response.features);
        }
      } else {
        setFeaturesToggles(toggles);
        setData(toggles);
      }
    } catch (error) {
      console.error("Error fetching features:", error);
      // If there's an error parsing localStorage or no data exists, fetch from API
      try {
        setIsLoading(true);
        const response = await appService.getFeatures();
        if (response?.features) {
          localStorage.setItem("featuresToggles", encryptData(JSON.stringify(response.features)));
          setFeaturesToggles(response.features);
          setData(response.features);
        }
      } catch (fetchError) {
        console.error("Error fetching features:", fetchError);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAndFetchFeatures();
  }, [checkAndFetchFeatures]);

  const handleCheckboxChange = (index) => {
    if (!data) return;
    const updatedData = [...data];
    updatedData[index].isVisible = !updatedData[index].isVisible;
    setData(updatedData);
  };

  const { showFeatureToggle } = useSelector((state) => state.login);

  const dispatch = useDispatch();

  const closeFeatureToggle = () => {
    if (!data) return;
    localStorage.setItem("featuresToggles", encryptData(JSON.stringify(data)));
    dispatch({
      type: userConstants.SHOW_FEATURE_TOGGLE,
      showFeatureToggle: false,
    });
  };

const [version, setVersion] = useState("");

  const getBuildVersion = useCallback(async () => {
    try {
      const versions = await homeServices.getBuildVersion();
      if (Array.isArray(versions) && versions.length > 0) {
        const { version: ver, build_no } = versions[0];
        setVersion(`${ver} (${build_no})`);
      } else {
        setVersion("Unknown");
        console.warn("build_version.json is empty or not an array:", versions);
      }
    } catch (err) {
      setVersion("Unknown");
      console.error("Failed to fetch build version:", err);
    }
  }, []);

  useEffect(() => {
    getBuildVersion();
  }, [getBuildVersion]);

  if (isLoading) {
    return null;
  }

  if (!data || !featuresToggles) {
    return null;
  }

  return (
    <>
      {showFeatureToggle && (
        <Popup
          closePopup={() =>
            dispatch({
              type: userConstants.SHOW_FEATURE_TOGGLE,
              showFeatureToggle: false,
            })
          }
        >
          <FeatureToggleWrapper>
            <h1>
              {t("SERVICE HATCH")}
            </h1>
            <p>
              Changes made here are temporary, are not transmitted to the web
              service, and do not directly affect the account data. Side-effects
              from features enabled/disabled here could potentially affect
              future service interactions. Use with care.
            </p>
            <a href="/">
              {t("Click here to get to Archive page for Release Notes")}
            </a>
            {version && (
              <a href="/">
                {t("Version")} {version}
              </a>
            )}
            <section>
              <h6>
                {t("Application Feature Toggles")}
              </h6>
              <small>
                {t("Default settings come from the application-specific feature toggle settings stored in the database.")}
              </small>
              <ul>
                <FeatureToggleWrapper>
                  {data.map((item, index) => (
                    <div key={index}>
                      <label>
                        <input
                          type="checkbox"
                          checked={item.isVisible}
                          onChange={() => handleCheckboxChange(index)}
                        />
                        {item.feature}
                      </label>
                    </div>
                  ))}
                </FeatureToggleWrapper>
              </ul>
              <Button handleOnClick={closeFeatureToggle} primary={true}>
                {t("Done")}
              </Button>
            </section>
          </FeatureToggleWrapper>
        </Popup>
      )}
    </>
  );
}

export default FeatureToggle;
